import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchAdminInventoryItem,
  fetchInventoryItemAssignments,
  fetchAdminUnits,
  fetchAdminRooms,
  fetchAdminAuditLogs,
  updateInventoryItem,
  deleteInventoryItem,
  createInventoryAssignment,
  updateInventoryAssignment,
  deleteInventoryAssignment,
  moveInventoryAssignment,
  createDamageReport,
  fetchDamageReports,
  patchDamageReport,
  normalizeUnit,
  fetchInventoryPhotos,
  uploadInventoryPhotos,
  patchInventoryPhoto,
  deleteInventoryPhoto,
  fetchDamagePhotos,
  uploadDamagePhotos,
  patchDamagePhoto,
  deleteDamagePhoto,
} from "../../api/adminData";
import PhotoGallery from "../../components/inventory/PhotoGallery";
import { formatDateOnly, todayBusinessDate } from "../../utils/datetime";
import InventoryDetailHeader from "./inventory/InventoryDetailHeader";
import {
  DetailCard,
  InventoryDetailInfoCard,
  InventoryDetailSupplierCard,
  InventoryStockCard,
} from "./inventory/InventoryDetailCards";
import InventoryAssignmentsSection from "./inventory/InventoryAssignmentsSection";
import InventoryNotesCard from "./inventory/InventoryNotesCard";
import {
  InventoryAssignCreateDialog,
  InventoryAssignEditDialog,
  InventoryAssignMoveDialog,
  InventoryAssignRemoveDialog,
} from "./inventory/InventoryAssignmentDialogs";
import { filterUnitsForAssignSearch } from "./inventory/InventoryUnitSearch";
import InventoryDamageSection from "./inventory/InventoryDamageSection";
import InventoryDamageDialog from "./inventory/InventoryDamageDialog";
import InventoryItemEditDialog from "./inventory/InventoryItemEditDialog";
import InventoryTimeline from "./inventory/InventoryTimeline";

const INVENTORY_CATEGORIES = [
  "Wohnzimmer",
  "Schlafzimmer",
  "Küche",
  "Esszimmer",
  "Badezimmer",
  "Balkon / Terrasse",
  "Büro",
  "Flur / Eingangsbereich",
  "Sonstiges",
];

const INVENTORY_CONDITION_OPTIONS = [
  "Neu",
  "Neuwertig",
  "Gebraucht",
  "Beschädigt",
  "Defekt",
];

function buildConditionSelectOptions(currentValue) {
  const v = String(currentValue || "").trim();
  if (v && !INVENTORY_CONDITION_OPTIONS.includes(v)) {
    return [v, ...INVENTORY_CONDITION_OPTIONS];
  }
  return [...INVENTORY_CONDITION_OPTIONS];
}

function buildCategorySelectOptions(currentValue) {
  const v = String(currentValue || "").trim();
  if (v && !INVENTORY_CATEGORIES.includes(v)) {
    return [v, ...INVENTORY_CATEGORIES];
  }
  return [...INVENTORY_CATEGORIES];
}

function normalizeInventoryCategory(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (INVENTORY_CATEGORIES.includes(s)) return s;
  const lower = s.toLowerCase();
  const hit = INVENTORY_CATEGORIES.find((c) => c.toLowerCase() === lower);
  return hit || s;
}

function parseOptionalProductUrl(raw) {
  if (raw == null || String(raw).trim() === "") return { value: null };
  const s = String(raw).trim();
  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return { error: "Produkt-URL muss mit http:// oder https:// beginnen." };
    }
    return { value: s };
  } catch {
    return { error: "Ungültige Produkt-URL." };
  }
}

/** Parse ISO date/datetime to local calendar Date (start of day). */
function parseInventoryDateLocal(iso) {
  if (iso == null || iso === "") return null;
  const s = String(iso).trim();
  const head = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const [y, m, d] = head.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return null;
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function todayLocalDate() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/**
 * Readable duration between two calendar dates (KPI F.6 style).
 * Under 30 days: "X Tage"; 1–11 months: "X Monate"; 12+ months: "X Jahre, Y Monate"
 */
function formatDurationDe(start, end) {
  if (!start || !end) return null;
  const dayMs = 86400000;
  const totalDays = Math.floor((end.getTime() - start.getTime()) / dayMs);
  if (totalDays < 0) return null;
  if (totalDays < 30) {
    if (totalDays === 0) return "0 Tage";
    if (totalDays === 1) return "1 Tag";
    return `${totalDays} Tage`;
  }
  let months =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;
  if (months < 12) {
    const m = Math.max(months, 1);
    return m === 1 ? "1 Monat" : `${m} Monate`;
  }
  const years = Math.floor(months / 12);
  const mo = months % 12;
  const yPart = years === 1 ? "1 Jahr" : `${years} Jahre`;
  if (mo === 0) return yPart;
  const mPart = mo === 1 ? "1 Monat" : `${mo} Monate`;
  return `${yPart}, ${mPart}`;
}

function itemTotalAgeLabel(it) {
  const start = parseInventoryDateLocal(it?.purchase_date);
  if (!start) return null;
  const end = it?.retired_at ? parseInventoryDateLocal(it.retired_at) : todayLocalDate();
  if (!end) return null;
  return formatDurationDe(start, end);
}

function assignmentUsageDurationLabel(it, row) {
  const start = parseInventoryDateLocal(row?.first_assigned_at);
  if (!start) return null;
  const end = it?.retired_at ? parseInventoryDateLocal(it.retired_at) : todayLocalDate();
  if (!end) return null;
  return formatDurationDe(start, end);
}

const emptyItemForm = () => ({
  name: "",
  category: "",
  brand: "",
  total_quantity: "1",
  condition: "",
  purchase_price_chf: "",
  purchase_date: "",
  supplier_article_number: "",
  purchased_from: "",
  product_url: "",
  notes: "",
});

function itemToForm(row) {
  if (!row) return emptyItemForm();
  return {
    name: row.name || "",
    category: normalizeInventoryCategory(row.category || ""),
    brand: row.brand || "",
    total_quantity: String(row.total_quantity ?? 1),
    condition: row.condition || "",
    purchase_price_chf: row.purchase_price_chf != null ? String(row.purchase_price_chf) : "",
    purchase_date: row.purchase_date ? String(row.purchase_date).slice(0, 10) : "",
    supplier_article_number: row.supplier_article_number || "",
    purchased_from: row.purchased_from || "",
    product_url: row.product_url || "",
    notes: row.notes || "",
  };
}

export default function AdminInventoryDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyItemForm());
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");

  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesErr, setNotesErr] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    unit_id: "",
    room_id: "",
    quantity: "1",
    notes: "",
  });
  const [assignRooms, setAssignRooms] = useState([]);
  const [assignErr, setAssignErr] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignUnitFilter, setAssignUnitFilter] = useState("");

  const [editAssignOpen, setEditAssignOpen] = useState(false);
  const [editingAsg, setEditingAsg] = useState(null);
  const [editAssignForm, setEditAssignForm] = useState({
    unit_id: "",
    room_id: "",
    quantity: "1",
    notes: "",
  });
  // Remove-reason dialog (replaces window.confirm on "Entfernen").
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeRow, setRemoveRow] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [removeErr, setRemoveErr] = useState("");
  const [removeSaving, setRemoveSaving] = useState(false);

  // Move dialog (end old stay + create new in the target unit/room).
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveRow, setMoveRow] = useState(null);
  const [moveForm, setMoveForm] = useState({ unit_id: "", room_id: "" });
  const [moveRooms, setMoveRooms] = useState([]);
  const [moveUnitFilter, setMoveUnitFilter] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [moveErr, setMoveErr] = useState("");
  const [moveSaving, setMoveSaving] = useState(false);

  const [damageReports, setDamageReports] = useState([]);
  const [damageReportsLoading, setDamageReportsLoading] = useState(false);
  const [damageReportsErr, setDamageReportsErr] = useState("");
  const [damageModalOpen, setDamageModalOpen] = useState(false);
  const [damageModalRow, setDamageModalRow] = useState(null);
  const [damageForm, setDamageForm] = useState({
    description: "",
    estimated_cost_chf: "",
    reported_at: "",
  });
  const [damageSaving, setDamageSaving] = useState(false);
  const [damageFormErr, setDamageFormErr] = useState("");
  const [damageStatusUpdatingId, setDamageStatusUpdatingId] = useState("");

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(true);
  const [auditLogError, setAuditLogError] = useState("");

  // Photos (Sprint 1).
  // Inventory-item gallery + per-damage gallery share the same component.
  // damagePhotosByDamageId maps damage report id -> photo list.
  const [inventoryPhotos, setInventoryPhotos] = useState([]);
  const [inventoryPhotosUploading, setInventoryPhotosUploading] = useState(false);
  const [inventoryPhotosBusy, setInventoryPhotosBusy] = useState(false);
  const [damagePhotosByDamageId, setDamagePhotosByDamageId] = useState({});
  const [damagePhotosUploadingId, setDamagePhotosUploadingId] = useState("");
  const [damagePhotosBusyId, setDamagePhotosBusyId] = useState("");

  const reloadDamageReports = useCallback(() => {
    if (!itemId) return Promise.resolve();
    setDamageReportsLoading(true);
    setDamageReportsErr("");
    return fetchDamageReports()
      .then((data) => {
        const items =
          data != null && typeof data === "object" && Array.isArray(data.items) ? data.items : [];
        const ids = new Set(assignments.map((a) => String(a.id)));
        const filtered = items.filter((r) => ids.has(String(r.assignment_id)));
        filtered.sort((a, b) => String(b.reported_at || "").localeCompare(String(a.reported_at || "")));
        setDamageReports(filtered);
      })
      .catch((e) => {
        setDamageReportsErr(e?.message || "Schäden konnten nicht geladen werden.");
        setDamageReports([]);
      })
      .finally(() => setDamageReportsLoading(false));
  }, [itemId, assignments]);

  const reloadAuditLogs = useCallback(() => {
    if (!itemId) return Promise.resolve();
    setAuditLogLoading(true);
    setAuditLogError("");
    return fetchAdminAuditLogs({ entity_type: "inventory_item", entity_id: itemId })
      .then((data) => {
        setAuditLogs(Array.isArray(data.items) ? data.items : []);
      })
      .catch((e) => {
        setAuditLogError(e?.message || "Verlauf konnte nicht geladen werden.");
        setAuditLogs([]);
      })
      .finally(() => setAuditLogLoading(false));
  }, [itemId]);

  // ── Photos ─────────────────────────────────────────────────────
  const reloadInventoryPhotos = useCallback(() => {
    if (!itemId) return Promise.resolve();
    return fetchInventoryPhotos(itemId)
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setInventoryPhotos(items);
      })
      .catch((e) => {
        toast.error(e?.message || "Fotos konnten nicht geladen werden.");
        setInventoryPhotos([]);
      });
  }, [itemId]);

  const handleUploadInventoryPhotos = useCallback(
    async (files) => {
      if (!itemId) return;
      setInventoryPhotosUploading(true);
      try {
        await uploadInventoryPhotos(itemId, files);
        await reloadInventoryPhotos();
        toast.success(
          files.length === 1 ? "Foto hochgeladen." : `${files.length} Fotos hochgeladen.`
        );
        reloadAuditLogs();
      } catch (e) {
        toast.error(e?.message || "Hochladen fehlgeschlagen.");
      } finally {
        setInventoryPhotosUploading(false);
      }
    },
    [itemId, reloadInventoryPhotos, reloadAuditLogs]
  );

  const handleDeleteInventoryPhoto = useCallback(
    async (photoId) => {
      setInventoryPhotosBusy(true);
      try {
        await deleteInventoryPhoto(photoId);
        await reloadInventoryPhotos();
        toast.success("Foto gelöscht.");
        reloadAuditLogs();
      } catch (e) {
        toast.error(e?.message || "Löschen fehlgeschlagen.");
      } finally {
        setInventoryPhotosBusy(false);
      }
    },
    [reloadInventoryPhotos, reloadAuditLogs]
  );

  const handleEditInventoryPhotoCaption = useCallback(
    async (photoId, caption) => {
      try {
        await patchInventoryPhoto(photoId, { caption: caption || "" });
        await reloadInventoryPhotos();
        toast.success("Beschreibung gespeichert.");
      } catch (e) {
        toast.error(e?.message || "Speichern fehlgeschlagen.");
      }
    },
    [reloadInventoryPhotos]
  );

  const reloadDamagePhotos = useCallback(async (damageId) => {
    if (!damageId) return;
    try {
      const data = await fetchDamagePhotos(damageId);
      const items = Array.isArray(data?.items) ? data.items : [];
      setDamagePhotosByDamageId((prev) => ({ ...prev, [damageId]: items }));
    } catch (e) {
      toast.error(e?.message || "Fotos konnten nicht geladen werden.");
      setDamagePhotosByDamageId((prev) => ({ ...prev, [damageId]: [] }));
    }
  }, []);

  const handleUploadDamagePhotos = useCallback(
    async (damageId, files) => {
      setDamagePhotosUploadingId(String(damageId));
      try {
        await uploadDamagePhotos(damageId, files);
        await reloadDamagePhotos(damageId);
        toast.success(
          files.length === 1 ? "Foto hochgeladen." : `${files.length} Fotos hochgeladen.`
        );
      } catch (e) {
        toast.error(e?.message || "Hochladen fehlgeschlagen.");
      } finally {
        setDamagePhotosUploadingId("");
      }
    },
    [reloadDamagePhotos]
  );

  const handleDeleteDamagePhoto = useCallback(
    async (damageId, photoId) => {
      setDamagePhotosBusyId(String(damageId));
      try {
        await deleteDamagePhoto(photoId);
        await reloadDamagePhotos(damageId);
        toast.success("Foto gelöscht.");
      } catch (e) {
        toast.error(e?.message || "Löschen fehlgeschlagen.");
      } finally {
        setDamagePhotosBusyId("");
      }
    },
    [reloadDamagePhotos]
  );

  const handleEditDamagePhotoCaption = useCallback(
    async (damageId, photoId, caption) => {
      try {
        await patchDamagePhoto(photoId, { caption: caption || "" });
        await reloadDamagePhotos(damageId);
        toast.success("Beschreibung gespeichert.");
      } catch (e) {
        toast.error(e?.message || "Speichern fehlgeschlagen.");
      }
    },
    [reloadDamagePhotos]
  );

  const load = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    setError("");
    Promise.all([
      fetchAdminInventoryItem(itemId),
      fetchInventoryItemAssignments(itemId).catch(() => []),
      fetchAdminUnits().catch(() => []),
    ])
      .then(([inv, asg, ulist]) => {
        setError("");
        if (!inv) {
          setItem(null);
          setError("Artikel nicht gefunden.");
          return;
        }
        setItem(inv);
        setNotesDraft(inv.notes || "");
        setAssignments(Array.isArray(asg) ? asg : []);
        setUnits(Array.isArray(ulist) ? ulist : []);
      })
      .catch((e) => {
        setError(e?.message || "Laden fehlgeschlagen.");
        setItem(null);
      })
      .finally(() => {
        setLoading(false);
        reloadAuditLogs();
      });
  }, [itemId, reloadAuditLogs]);

  useEffect(() => {
    load();
  }, [load, location.key]);

  useEffect(() => {
    if (!itemId || loading) return;
    void reloadDamageReports();
  }, [itemId, loading, reloadDamageReports]);

  // Inventory photos: load once item is ready.
  useEffect(() => {
    if (!itemId || loading) return;
    void reloadInventoryPhotos();
  }, [itemId, loading, reloadInventoryPhotos]);

  // Damage photos: refresh each damage row's gallery when the damage list
  // changes (initial load, status change, new damage created).
  useEffect(() => {
    if (!damageReports.length) return;
    for (const r of damageReports) {
      if (!r?.id) continue;
      void reloadDamagePhotos(r.id);
    }
  }, [damageReports, reloadDamagePhotos]);

  useEffect(() => {
    if (!assignOpen) {
      setAssignRooms([]);
      return;
    }
    const uid = assignForm.unit_id;
    if (!uid) {
      setAssignRooms([]);
      return;
    }
    fetchAdminRooms(uid)
      .then((r) => setAssignRooms(Array.isArray(r) ? r : []))
      .catch(() => setAssignRooms([]));
  }, [assignOpen, assignForm.unit_id]);

  useEffect(() => {
    if (!moveOpen) {
      setMoveRooms([]);
      return;
    }
    const uid = moveForm.unit_id;
    if (!uid) {
      setMoveRooms([]);
      return;
    }
    fetchAdminRooms(uid)
      .then((r) => setMoveRooms(Array.isArray(r) ? r : []))
      .catch(() => setMoveRooms([]));
  }, [moveOpen, moveForm.unit_id]);

  function openEdit() {
    if (!item) return;
    setForm(itemToForm(item));
    setFormErr("");
    setEditOpen(true);
  }

  function bodyFromForm() {
    const tq = Math.max(1, parseInt(form.total_quantity, 10) || 1);
    const pu = parseOptionalProductUrl(form.product_url);
    if (pu.error) return { _error: pu.error };
    return {
      name: String(form.name || "").trim(),
      category: String(form.category || "").trim(),
      brand: String(form.brand || "").trim() || null,
      total_quantity: tq,
      condition: String(form.condition || "").trim(),
      purchase_price_chf:
        form.purchase_price_chf === "" || form.purchase_price_chf == null
          ? null
          : Number(form.purchase_price_chf),
      purchase_date: form.purchase_date ? form.purchase_date : null,
      supplier_article_number: String(form.supplier_article_number || "").trim() || null,
      purchased_from: String(form.purchased_from || "").trim() || null,
      product_url: pu.value,
      notes: String(form.notes || "").trim() || null,
    };
  }

  async function submitEdit(e) {
    e.preventDefault();
    if (!itemId) return;
    const b = bodyFromForm();
    if (b._error) {
      setFormErr(b._error);
      return;
    }
    if (!b.name) {
      setFormErr("Name erforderlich.");
      return;
    }
    setSaving(true);
    setFormErr("");
    try {
      const updated = await updateInventoryItem(itemId, b);
      setItem(updated);
      setNotesDraft(updated.notes || "");
      setEditOpen(false);
      void reloadAuditLogs();
    } catch (err) {
      setFormErr(err?.message || "Fehler.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    if (!window.confirm(`Artikel «${item.name}» inkl. Zuordnungen löschen?`)) return;
    try {
      await deleteInventoryItem(itemId);
      navigate("/admin/inventory");
    } catch (err) {
      window.alert(err?.message || "Löschen fehlgeschlagen.");
    }
  }

  async function saveNotes() {
    if (!itemId) return;
    setNotesSaving(true);
    setNotesErr("");
    try {
      const updated = await updateInventoryItem(itemId, {
        notes: notesDraft.trim() || null,
      });
      setItem(updated);
      setNotesDraft(updated.notes || "");
      void reloadAuditLogs();
    } catch (err) {
      setNotesErr(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setNotesSaving(false);
    }
  }

  function openAssign() {
    const u0 = units[0];
    const nu = u0 ? normalizeUnit(u0) : null;
    const firstUnit = nu ? String(nu.id || nu.unitId || "") : "";
    setAssignForm({
      unit_id: firstUnit,
      room_id: "",
      quantity: "1",
      notes: "",
    });
    setAssignErr("");
    setAssignUnitFilter("");
    setAssignOpen(true);
  }

  async function submitAssign(e) {
    e.preventDefault();
    if (!itemId || !assignForm.unit_id) {
      setAssignErr("Unit wählen.");
      return;
    }
    const qty = Math.max(1, parseInt(assignForm.quantity, 10) || 1);
    const avail = item?.available ?? 0;
    if (qty > avail) {
      setAssignErr(`Max. verfügbar: ${avail}`);
      return;
    }
    setAssignSaving(true);
    setAssignErr("");
    try {
      await createInventoryAssignment(itemId, {
        unit_id: assignForm.unit_id,
        room_id: assignForm.room_id || null,
        quantity: qty,
        notes: assignForm.notes.trim() || null,
      });
      setAssignOpen(false);
      setAssignUnitFilter("");
      load();
    } catch (err) {
      setAssignErr(err?.message || "Zuordnung fehlgeschlagen.");
    } finally {
      setAssignSaving(false);
    }
  }

  function openEditAssignment(row) {
    setAssignErr("");
    setEditingAsg(row);
    // Location (unit/room) is changed via the move dialog, not here — edit only
    // touches quantity + notes on the current row.
    setEditAssignForm({
      unit_id: "",
      room_id: "",
      quantity: String(row.quantity ?? 1),
      notes: row.notes || "",
    });
    setEditAssignOpen(true);
  }

  async function submitEditAssignment(e) {
    e.preventDefault();
    if (!editingAsg) return;
    const qty = Math.max(1, parseInt(editAssignForm.quantity, 10) || 1);
    const avail = item?.available ?? 0;
    const maxQty = avail + (editingAsg.quantity ?? 0);
    if (qty > maxQty) {
      setAssignErr(`Max. für diese Position: ${maxQty}`);
      return;
    }
    setAssignSaving(true);
    setAssignErr("");
    try {
      await updateInventoryAssignment(editingAsg.id, {
        quantity: qty,
        notes: editAssignForm.notes.trim() || null,
      });
      setEditAssignOpen(false);
      setEditingAsg(null);
      load();
    } catch (err) {
      setAssignErr(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setAssignSaving(false);
    }
  }

  function openRemove(row) {
    setRemoveRow(row);
    setRemoveReason("");
    setRemoveErr("");
    setRemoveOpen(true);
  }

  async function submitRemove() {
    if (!removeRow) return;
    setRemoveSaving(true);
    setRemoveErr("");
    try {
      await deleteInventoryAssignment(removeRow.id, {
        reason: removeReason.trim() || null,
      });
      setRemoveOpen(false);
      setRemoveRow(null);
      load();
    } catch (err) {
      setRemoveErr(err?.message || "Entfernen fehlgeschlagen.");
    } finally {
      setRemoveSaving(false);
    }
  }

  function openMove(row) {
    setMoveRow(row);
    setMoveForm({ unit_id: "", room_id: "" });
    setMoveUnitFilter("");
    setMoveReason("");
    setMoveErr("");
    setMoveOpen(true);
  }

  async function submitMove(e) {
    e.preventDefault();
    if (!moveRow || !moveForm.unit_id) {
      setMoveErr("Zielwohnung wählen.");
      return;
    }
    setMoveSaving(true);
    setMoveErr("");
    try {
      await moveInventoryAssignment(moveRow.id, {
        unit_id: moveForm.unit_id,
        room_id: moveForm.room_id || null,
        reason: moveReason.trim() || null,
      });
      setMoveOpen(false);
      setMoveRow(null);
      setMoveUnitFilter("");
      load();
    } catch (err) {
      setMoveErr(err?.message || "Verschieben fehlgeschlagen.");
    } finally {
      setMoveSaving(false);
    }
  }

  function openDamageModal(row) {
    setDamageModalRow(row);
    setDamageForm({
      description: "",
      estimated_cost_chf: "",
      reported_at: todayBusinessDate(),
    });
    setDamageFormErr("");
    setDamageModalOpen(true);
  }

  async function submitDamageReport(e) {
    e.preventDefault();
    if (!damageModalRow) return;
    const desc = String(damageForm.description || "").trim();
    if (!desc) {
      setDamageFormErr("Beschreibung erforderlich.");
      return;
    }
    let estimated = null;
    if (damageForm.estimated_cost_chf !== "" && damageForm.estimated_cost_chf != null) {
      const n = Number(damageForm.estimated_cost_chf);
      if (Number.isFinite(n)) estimated = n;
    }
    setDamageSaving(true);
    setDamageFormErr("");
    try {
      await createDamageReport({
        assignment_id: damageModalRow.id,
        reported_at: damageForm.reported_at || todayBusinessDate(),
        description: desc,
        estimated_cost_chf: estimated,
      });
      setDamageModalOpen(false);
      setDamageModalRow(null);
      load();
    } catch (err) {
      setDamageFormErr(err?.message || "Schaden konnte nicht gemeldet werden.");
    } finally {
      setDamageSaving(false);
    }
  }

  async function handleDamageStatusChange(reportId, newStatus, previousStatus) {
    if (!reportId || String(newStatus) === String(previousStatus)) return;
    setDamageStatusUpdatingId(String(reportId));
    try {
      await patchDamageReport(reportId, { damage_status: newStatus });
      load();
    } catch (err) {
      window.alert(err?.message || "Status konnte nicht geändert werden.");
    } finally {
      setDamageStatusUpdatingId("");
    }
  }

  const assignmentById = useMemo(() => {
    const m = new Map();
    for (const a of assignments) {
      m.set(String(a.id), a);
    }
    return m;
  }, [assignments]);

  const unitById = useMemo(() => {
    const m = new Map();
    for (const u of units) {
      const nu = normalizeUnit(u);
      m.set(String(nu.id || nu.unitId), nu);
    }
    return m;
  }, [units]);

  const unitsFilteredForAssign = useMemo(
    () => filterUnitsForAssignSearch(units, assignUnitFilter, assignForm.unit_id),
    [units, assignUnitFilter, assignForm.unit_id]
  );

  const unitsFilteredForMove = useMemo(
    () => filterUnitsForAssignSearch(units, moveUnitFilter, moveForm.unit_id),
    [units, moveUnitFilter, moveForm.unit_id]
  );

  /* The two early returns are part of the shell S6b owns, so they move to
     tokens with the rest of it. Without this the page would be half-migrated:
     a loading or not-found state still painting itself dark and relying on the
     light-mode !important block to look right. Text and affordances are
     unchanged — D56/D57/D58 pin them. */
  if (loading) {
    return (
      <div
        data-admin-page="inventory-detail"
        aria-busy="true"
        className="min-h-[40vh] py-10 text-[13px] font-medium text-muted-foreground"
      >
        Lade …
      </div>
    );
  }

  if (error && !item) {
    return (
      <div data-admin-page="inventory-detail" className="min-h-[40vh] py-10 text-foreground">
        <p role="alert" className="m-0 text-sm text-destructive">
          {error}
        </p>
        <Link
          to="/admin/inventory"
          className="mt-4 inline-flex rounded-[8px] border border-border bg-card px-3.5 py-2 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Zurück zum Inventar
        </Link>
      </div>
    );
  }

  if (!item) return null;

  const total = item.total_quantity ?? 1;
  const assigned = item.assigned_total ?? 0;
  const available = item.available ?? 0;

  return (
    <div data-admin-page="inventory-detail" className="flex flex-col gap-5">
      {/* The page no longer paints itself: no min-h-screen, no hex background.
          AdminLayout already wraps the outlet in p-4 md:p-10, so sitting
          inside the shell is what finally makes light mode work here — the
          same move the overview made in S4.

          data-admin-page STILL STAYS after S7b, for one reason only:
          PhotoGallery is rendered inside this subtree (the Fotos card and
          every damage strip) and is the last file on the page still written
          in hex, so the remaining six light-mode rules in index.css have to
          keep reaching it. Every section and every dialog is on tokens now.
          Tokenising PhotoGallery retires the block and this attribute with
          it — it has no other reader. */}
      <InventoryDetailHeader
        item={item}
        /* First gallery entry, already in state — no request of its own. */
        thumbUrl={inventoryPhotos[0] ? inventoryPhotos[0].thumb_url : null}
        onEdit={openEdit}
        onDelete={handleDelete}
      />

      {error ? (
        <p role="alert" className="m-0 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── Workspace ──────────────────────────────────────────────────────
          Main column plus the Verlauf aside, the reference's shape — but only
          from 2xl. The breakpoint is measured, not picked: AdminSidebar is
          288px (SIDEBAR_WIDTH_CLASS) and AdminLayout pads the outlet with
          md:p-10, so the content box is `viewport − 368`.

            xl  (1280) → 912px content. A ~320px aside leaves the seven-column
                         Zuordnungen table under 600px — exactly the squeeze
                         this slice exists to remove.
            2xl (1536) → 1168px content, ~830px main. The table keeps its own
                         overflow-x wrapper but most of it is readable, and
                         from ~1900px it needs no scroller at all.

          Below 2xl the aside simply stacks under the main column at full
          width. Either way it is mounted exactly once — one tree, no CSS
          duplicate, same as the assignment surfaces. */}
      <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* Stammdaten wide, Produkt / Lieferant narrower beside it.
              minmax(0,…) on both tracks because the children carry long
              unbroken strings (unit ids, product URLs) that `auto` would let
              push the track. */}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-start">
            <InventoryDetailInfoCard
              item={item}
              ageLabel={itemTotalAgeLabel(item) || null}
              retiredAtLabel={item.retired_at ? formatDateOnly(item.retired_at) : null}
              purchaseDateLabel={item.purchase_date ? formatDateOnly(item.purchase_date) : null}
            />
            <InventoryDetailSupplierCard item={item} />
          </div>

          <InventoryStockCard total={total} assigned={assigned} available={available} />

          {/* Fotos before Zuordnungen, as the reference orders them: you
              recognise the article, then see where its pieces are. A flat,
              wrapping strip of 112x84 thumbnails — no hero, no empty stage. */}
          <DetailCard title="Fotos">
            <PhotoGallery
              photos={inventoryPhotos}
              onUpload={handleUploadInventoryPhotos}
              onDelete={handleDeleteInventoryPhoto}
              onCaptionSave={handleEditInventoryPhotoCaption}
              uploading={inventoryPhotosUploading}
              busy={inventoryPhotosBusy}
              maxPhotos={20}
              emptyHint="Noch keine Fotos für diesen Artikel."
            />
          </DetailCard>

          <InventoryAssignmentsSection
            assignments={assignments}
            available={available}
            error={assignErr && (assignOpen || editAssignOpen) ? assignErr : ""}
            unitById={unitById}
            usageDurationFor={(row) => assignmentUsageDurationLabel(item, row)}
            onAdd={openAssign}
            onReportDamage={openDamageModal}
            onMove={openMove}
            onEdit={openEditAssignment}
            onRemove={openRemove}
          />

          <InventoryDamageSection
            reports={damageReports}
            loading={damageReportsLoading}
            error={damageReportsErr}
            assignmentById={assignmentById}
            unitById={unitById}
            photosByDamageId={damagePhotosByDamageId}
            uploadingId={damagePhotosUploadingId}
            busyId={damagePhotosBusyId}
            statusUpdatingId={damageStatusUpdatingId}
            onUploadPhotos={handleUploadDamagePhotos}
            onDeletePhoto={handleDeleteDamagePhoto}
            onCaptionSave={handleEditDamagePhotoCaption}
            onStatusChange={handleDamageStatusChange}
          />

          <InventoryNotesCard
            notesDraft={notesDraft}
            onChangeDraft={setNotesDraft}
            onSave={saveNotes}
            saving={notesSaving}
            error={notesErr}
          />
        </div>

        <InventoryTimeline
          logs={auditLogs}
          loading={auditLogLoading}
          error={auditLogError}
          unitById={unitById}
        />
      </div>

      {editOpen && (
        <InventoryItemEditDialog
          item={item}
          form={form}
          onChangeForm={setForm}
          categoryOptions={buildCategorySelectOptions(form.category)}
          conditionOptions={buildConditionSelectOptions(form.condition).map((c) => ({
            value: c,
            label: INVENTORY_CONDITION_OPTIONS.includes(c) ? c : `${c} (bestehend)`,
          }))}
          error={formErr}
          saving={saving}
          onSubmit={submitEdit}
          onCancel={() => {
            setEditOpen(false);
            setFormErr("");
          }}
        />
      )}

      {assignOpen && (
        <InventoryAssignCreateDialog
          error={assignErr}
          form={assignForm}
          onChangeForm={setAssignForm}
          rooms={assignRooms}
          saving={assignSaving}
          unitFilter={assignUnitFilter}
          onChangeUnitFilter={setAssignUnitFilter}
          filteredUnits={unitsFilteredForAssign}
          available={available}
          onSubmit={submitAssign}
          onCancel={() => {
            setAssignOpen(false);
            setAssignErr("");
            setAssignUnitFilter("");
          }}
        />
      )}

      {editAssignOpen && editingAsg && (
        <InventoryAssignEditDialog
          error={assignErr}
          form={editAssignForm}
          onChangeForm={setEditAssignForm}
          saving={assignSaving}
          onSubmit={submitEditAssignment}
          onCancel={() => {
            setEditAssignOpen(false);
            setEditingAsg(null);
            setAssignErr("");
          }}
        />
      )}

      {removeOpen && removeRow && (
        <InventoryAssignRemoveDialog
          error={removeErr}
          reason={removeReason}
          onChangeReason={setRemoveReason}
          saving={removeSaving}
          onConfirm={submitRemove}
          onCancel={() => {
            setRemoveOpen(false);
            setRemoveRow(null);
            setRemoveErr("");
          }}
        />
      )}

      {moveOpen && moveRow && (
        <InventoryAssignMoveDialog
          row={moveRow}
          unitById={unitById}
          error={moveErr}
          form={moveForm}
          onChangeForm={setMoveForm}
          rooms={moveRooms}
          saving={moveSaving}
          unitFilter={moveUnitFilter}
          onChangeUnitFilter={setMoveUnitFilter}
          filteredUnits={unitsFilteredForMove}
          reason={moveReason}
          onChangeReason={setMoveReason}
          onSubmit={submitMove}
          onCancel={() => {
            setMoveOpen(false);
            setMoveRow(null);
            setMoveErr("");
            setMoveUnitFilter("");
          }}
        />
      )}

      {damageModalOpen && damageModalRow && (
        <InventoryDamageDialog
          row={damageModalRow}
          unitById={unitById}
          form={damageForm}
          onChangeForm={setDamageForm}
          error={damageFormErr}
          saving={damageSaving}
          onSubmit={submitDamageReport}
          onCancel={() => {
            setDamageModalOpen(false);
            setDamageModalRow(null);
            setDamageFormErr("");
          }}
        />
      )}
    </div>
  );
}
